import { toApiError } from "./classifyFailure";
import type { Backend, Credentials, ServerSettings, TaskApi } from "./taskApi";
import { TauriTaskApi, type TauriBridge } from "./tauriTaskApi";

/** Sign-in state lives in the Rust shell, which keeps the replica open between launches. */
export class DesktopBackend implements Backend {
  readonly kind = "desktop";

  constructor(private readonly bridge: TauriBridge) {}

  async savedServer(): Promise<ServerSettings | undefined> {
    const saved = await this.bridge.invoke<ServerSettings | null>("saved_server");
    return saved ?? undefined;
  }

  async resume(): Promise<TaskApi | undefined> {
    const signedIn = await this.bridge.invoke<boolean>("session_open");
    return signedIn ? this.openApi() : undefined;
  }

  async signIn(credentials: Credentials): Promise<TaskApi> {
    try {
      await this.bridge.invoke("sign_in", { ...credentials });
    } catch (failure) {
      throw toApiError(failure);
    }
    return this.openApi();
  }

  async signOut(): Promise<void> {
    await this.bridge.invoke("sign_out");
  }

  private async openApi(): Promise<TaskApi> {
    const api = new TauriTaskApi(this.bridge);
    await api.start();
    return api;
  }
}

export async function tauriBridge(): Promise<TauriBridge> {
  const [{ invoke }, { listen }] = await Promise.all([
    import("@tauri-apps/api/core"),
    import("@tauri-apps/api/event"),
  ]);
  return {
    invoke: (command, args) => invoke(command, args),
    listen: (event, handler) => listen(event, (message) => handler(message.payload)),
  };
}
